import { userErrorMessage } from '@wx-weiqi/contracts';
import { useEffect, useId, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, Cross2Icon, UploadIcon } from '@radix-ui/react-icons';
import type { ClassroomSgfData } from '@wx-weiqi/contracts';
import { MAX_CLASSROOM_SGF_FILES, type ClassroomSgfImportFailure } from '../stores/fox-kifu-store';
import './classroom-sgf-panel.css';

export function ClassroomSgfPanel({
  records,
  selected,
  failures,
  busy,
  uploading,
  onSelect,
  onUpload,
  onClose,
}: {
  readonly records: readonly ClassroomSgfData[];
  readonly selected: ClassroomSgfData | null;
  readonly failures: readonly ClassroomSgfImportFailure[];
  readonly busy: boolean;
  readonly uploading: boolean;
  readonly onSelect: (record: ClassroomSgfData) => void;
  readonly onUpload: () => void;
  readonly onClose: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [failureOpen, setFailureOpen] = useState(false);
  const panelRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const selectedIndex = selected === null ? -1 : records.indexOf(selected);
  const current = records[selectedIndex];

  useEffect(() => {
    if (!expanded) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !panelRef.current?.contains(event.target))
        setExpanded(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [expanded]);

  return (
    <aside
      ref={panelRef}
      className="studio-sgf-panel"
      role="dialog"
      aria-label="SGF棋谱文件"
      aria-busy={busy}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        if (expanded || failureOpen) {
          setExpanded(false);
          setFailureOpen(false);
          toggleRef.current?.focus();
        } else onClose();
      }}
    >
      <div className="studio-sgf-toolbar">
        <button
          ref={toggleRef}
          className="studio-sgf-toggle"
          type="button"
          aria-label={expanded ? '收起SGF文件列表' : '展开SGF文件列表'}
          aria-expanded={expanded}
          aria-controls={listId}
          disabled={records.length === 0 || busy}
          title={current?.filename}
          onClick={() => setExpanded((value) => !value)}
        >
          <span className="studio-sgf-tag">SGF</span>
          <span className="studio-sgf-filename">
            {uploading
              ? '正在上传棋谱…'
              : (current?.filename ?? (records.length > 0 ? '请选择棋谱' : '选择本地棋谱'))}
          </span>
          {records.length > 0 ? (
            <small>
              {selectedIndex >= 0 ? `${selectedIndex + 1} / ` : ''}
              {records.length}
            </small>
          ) : null}
          <ChevronDownIcon className={expanded ? 'is-expanded' : ''} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="studio-sgf-upload"
          aria-label={records.length > 0 ? '重新上传SGF文件' : '选择SGF文件'}
          title={`可多选，最多${MAX_CLASSROOM_SGF_FILES}个，每个不超过1MB`}
          disabled={busy}
          onClick={() => {
            setExpanded(false);
            setFailureOpen(false);
            onUpload();
          }}
        >
          <UploadIcon aria-hidden="true" />
          <span>{records.length > 0 ? '重新上传' : '上传'}</span>
        </button>
        {failures.length > 0 ? (
          <button
            className="studio-sgf-failure-toggle"
            type="button"
            aria-label="查看SGF上传问题"
            aria-expanded={failureOpen}
            onClick={() => setFailureOpen((value) => !value)}
          >
            {failures[0]?.filename === '' ? '上传提示' : `${failures.length} 未上传`}
          </button>
        ) : null}
        <button
          className="studio-sgf-close"
          type="button"
          aria-label="收起SGF文件窗口"
          title="收起窗口，保留棋谱"
          onClick={onClose}
        >
          <Cross2Icon aria-hidden="true" />
        </button>
      </div>
      {expanded ? (
        <div id={listId} className="studio-sgf-list" role="group" aria-label="已上传SGF文件列表">
          <p>已上传 {records.length} 个棋谱 · 选择后自动收起</p>
          <div className="studio-sgf-list-scroll">
            {records.map((record, index) => (
              <button
                key={index}
                type="button"
                aria-pressed={record === selected}
                disabled={busy}
                title={record.filename}
                onClick={() => {
                  onSelect(record);
                  setExpanded(false);
                  setFailureOpen(false);
                  toggleRef.current?.focus();
                }}
              >
                <span className="studio-sgf-index">{index + 1}</span>
                <span className="studio-sgf-file-detail">
                  <strong>{record.filename}</strong>
                  <small>
                    {record.record.boardSize} 路 · {record.frames.length - 1} 手
                  </small>
                </span>
                {record === selected ? <CheckIcon aria-hidden="true" /> : null}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {failures.length > 0 ? (
        <div className={failureOpen ? 'studio-sgf-failures' : 'sr-only'}>
          <p role="alert">
            {failures[0]?.filename === ''
              ? userErrorMessage(failures[0])
              : `${failures.length} 个文件未能上传，点击查看原因`}
          </p>
          {failureOpen ? (
            <ul>
              {failures
                .filter((failure) => failure.filename !== '')
                .map((failure, index) => (
                  <li key={index}>
                    <strong>{failure.filename}</strong>：{userErrorMessage(failure)}
                  </li>
                ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </aside>
  );
}
